import React, { useState, useEffect } from 'react';
import { useSupabase } from "@/hooks/useSupabase";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useArticleInteractions } from "@/hooks/useArticleInteractions";
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  user_name?: string;
}

interface ArticleCommentsProps {
  articleId: string;
}

export const ArticleComments = ({ articleId }: ArticleCommentsProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { user, isLoggedIn } = useSupabase();
  const { toast } = useToast();
  const { handleComment } = useArticleInteractions(articleId);
  const [isMockArticle, setIsMockArticle] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if this is a mock article (numeric ID)
    setIsMockArticle(/^\d+$/.test(articleId));
    if (!isLoggedIn) {
      setIsLoading(false);
      return;
    }
    fetchComments();
  }, [articleId, isLoggedIn]);

  const fetchComments = async () => {
    try {
      // For mock articles (numeric IDs), we'll use local storage
      if (/^\d+$/.test(articleId)) {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const storedMockComments = localStorage.getItem(`mockComments_${articleId}`);
        const mockComments = storedMockComments ? JSON.parse(storedMockComments) : [];
        setComments(mockComments);
        setIsLoading(false);
        return;
      }

      // For real articles with UUIDs, fetch from the database
      const { data, error } = await supabase
        .from('article_comments')
        .select('*')
        .eq('article_id', articleId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // If we have comments data, fetch the user names separately
      if (data && data.length > 0) {
        const commentsWithUserNames = await Promise.all(
          data.map(async (comment) => {
            if (comment.user_id) {
              // Fetch user profile separately to avoid join issues
              const { data: profileData } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('id', comment.user_id)
                .maybeSingle();
                
              return {
                ...comment,
                user_name: profileData?.full_name || 'Anonymous'
              };
            }
            return {
              ...comment,
              user_name: 'Anonymous'
            };
          })
        );
        
        setComments(commentsWithUserNames);
      } else {
        setComments(data || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: "Error",
        description: "Failed to load comments",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitComment = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Authentication Required",
        description: "Please login to comment on articles",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    if (!newComment.trim()) return;

    try {
      const success = await handleComment(newComment);
      if (success) {
        setNewComment("");
        fetchComments();
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: "Error",
        description: "Failed to submit comment",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <div className="mt-8 text-center">Loading comments...</div>;
  }

  return (
    <div className="mt-8 space-y-6">
      <h2 className="text-2xl font-bold">Comments</h2>
      
      {!isLoggedIn ? (
        <div className="p-4 border rounded-lg bg-muted/50">
          <p className="text-center text-muted-foreground">
            Please{' '}
            <Button 
              variant="link" 
              className="p-0 text-ai-indigo hover:text-ai-purple"
              onClick={() => navigate('/login')}
            >
              sign in
            </Button>
            {' '}to view and post comments.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <Textarea
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="min-h-[100px]"
            />
            <Button onClick={onSubmitComment}>
              Post Comment
            </Button>
          </div>

          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-muted-foreground">No comments yet.</p>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.id}
                  className="p-4 border rounded-lg space-y-2"
                >
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">
                      {comment.user_name || 'Anonymous'} • {format(new Date(comment.created_at), 'PPpp')}
                    </p>
                  </div>
                  <p>{comment.content}</p>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
